/**
 * ProvidersPage — /providers (spec 013 — Service Provider lifecycle admin)
 *
 * Paginated, searchable roster of Service Providers (ServiceManager.API
 * cross-tenant list) with an account-lifecycle drill-down drawer. Row click
 * opens a drawer that fetches the fresh provider detail (including the
 * append-only audit trail) and offers Suspend / Reactivate:
 *   - Suspend prompts for a reason (min 10 chars) and takes effect on the
 *     provider's very next request.
 *   - Reactivate clears the suspension (simple confirm, no reason body).
 *
 * Mirrors UserRosterPage (Table + Drawer + Toast + useSearchParams URL state)
 * and agentAdminService's ServiceManager host wiring.
 */

import { useCallback, useEffect, useState, startTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getProviders,
  getProviderDetail,
  getProviderAudit,
  suspendProvider,
  reactivateProvider,
  ProviderNotFoundError,
  ProviderConflictError,
  type ProviderAccountStatus,
  type ProviderAdminListItem,
  type ProviderAdminDetail,
  type ProviderAuditEntry,
} from '../services/providerAdminService';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Table, type Column } from '../components/Table';
import { Drawer } from '../components/Drawer';
import { Toast, type ToastState } from '../components/Toast';

const PAGE_SIZE = 25;
const MIN_REASON_LEN = 10;
const AUDIT_PAGE_SIZE = 25;

type StatusFilter = 'All' | ProviderAccountStatus;
const STATUS_FILTERS: StatusFilter[] = ['All', 'Active', 'Suspended'];

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function displayLabel(p: ProviderAdminListItem): string {
  return (
    p.companyName?.trim() ||
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() ||
    p.email ||
    `SPID ${p.spid}`
  );
}

function contactLabel(p: ProviderAdminListItem): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: ProviderAccountStatus }) {
  const cls =
    status === 'Suspended'
      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function VerificationBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-zinc-500">—</span>;
  const colours: Record<string, string> = {
    verified: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    pending: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  const cls = colours[status.toLowerCase()] ?? 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Suspend reason modal — mounted only while pending so local state resets.
// ---------------------------------------------------------------------------

function SuspendModal({
  providerLabel,
  onConfirm,
  onCancel,
}: {
  providerLabel: string;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= MIN_REASON_LEN;

  async function submit() {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Suspend provider"
        className="relative z-10 w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl"
      >
        <h2 className="text-sm font-semibold text-zinc-100">Suspend provider</h2>
        <p className="mt-1 text-xs text-zinc-400 break-words">{providerLabel}</p>
        <p className="mt-2 text-xs text-amber-400/90">
          Suspension takes effect immediately — the provider is locked out of all
          provider APIs and excluded from bidding, search, and matching until reactivated.
        </p>

        <label className="mt-4 block text-xs text-zinc-400" htmlFor="suspend-reason">
          Reason (required, min {MIN_REASON_LEN} characters)
        </label>
        <textarea
          id="suspend-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Why is this provider being suspended?"
          className="mt-1 w-full px-3 py-2 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 resize-y"
        />
        {!valid && reason.length > 0 && reason.trim().length < MIN_REASON_LEN && (
          <p className="mt-1 text-xs text-amber-400">
            {MIN_REASON_LEN - reason.trim().length} more character(s) needed.
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            disabled={!valid || saving}
            onClick={() => void submit()}
            className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {saving ? 'Working…' : 'Confirm Suspend'}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit trail list (recent entries + load-more via the paged audit endpoint)
// ---------------------------------------------------------------------------

function AuditTrail({ spid, initial }: { spid: number; initial: ProviderAuditEntry[] }) {
  const [entries, setEntries] = useState<ProviderAuditEntry[]>(initial);
  const [page, setPage] = useState(0); // 0 = only the initial (detail-embedded) slice
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasMore = total === null ? initial.length >= 10 : entries.length < total;

  async function loadMore() {
    if (loading) return;
    setLoading(true);
    setError(null);
    const nextPage = page + 1;
    try {
      const result = await getProviderAudit(spid, nextPage, AUDIT_PAGE_SIZE);
      setTotal(result.totalCount);
      setPage(nextPage);
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.auditLogId));
        return [...prev, ...result.items.filter((e) => !seen.has(e.auditLogId))];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }

  if (entries.length === 0) {
    return <p className="text-xs text-zinc-500">No audit history.</p>;
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {entries.map((entry) => (
          <li key={entry.auditLogId} className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-zinc-200">{entry.action}</span>
              <span className="text-xs font-mono text-zinc-500 whitespace-nowrap">
                {formatDate(entry.createdAt)}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">
              {entry.actorType ?? 'Unknown'}
              {entry.actorId ? ` — ${entry.actorId}` : ''}
            </p>
            {entry.details && (
              <p className="text-xs text-zinc-400 mt-1 break-words">{entry.details}</p>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="text-xs text-red-400">{error}</p>}
      {hasMore && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void loadMore()}
          className="w-full min-h-[48px] px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 text-xs font-medium hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Loading…' : 'Load full audit trail'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer body — fetches fresh detail; keyed by spid so it remounts per target.
// ---------------------------------------------------------------------------

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-zinc-500 text-xs">{label}</span>
      <p className="text-zinc-300 text-xs break-words">{value ?? '—'}</p>
    </div>
  );
}

function ProviderDetailBody({
  spid,
  isPending,
  onSuspend,
  onReactivate,
  refreshKey,
}: {
  spid: number;
  isPending: boolean;
  onSuspend: (detail: ProviderAdminDetail) => void;
  onReactivate: (detail: ProviderAdminDetail) => void;
  /** Bumped after a lifecycle action so the detail (and audit) refetch. */
  refreshKey: number;
}) {
  // The fetch result is tagged with the refreshKey it was loaded for; loading
  // is derived (no synchronous setState inside the effect body).
  const [result, setResult] = useState<{
    key: number;
    detail: ProviderAdminDetail | null;
    error: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProviderDetail(spid)
      .then((d) => {
        if (!cancelled) setResult({ key: refreshKey, detail: d, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResult({
            key: refreshKey,
            detail: null,
            error:
              err instanceof ProviderNotFoundError
                ? 'Provider not found — it may have been removed.'
                : err instanceof Error
                ? err.message
                : 'Failed to load provider',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [spid, refreshKey]);

  const loading = result === null || result.key !== refreshKey;
  const detail = loading ? null : result.detail;
  const error = loading ? null : result.error;

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-4 bg-zinc-800 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !detail) {
    return <p className="text-xs text-red-400">{error ?? 'Failed to load provider'}</p>;
  }

  const suspended = detail.accountStatus === 'Suspended';

  return (
    <div className="space-y-4">
      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-3">
        <DetailField label="Company" value={detail.companyName} />
        <DetailField label="Contact" value={contactLabel(detail) || '—'} />
        <DetailField label="Email" value={detail.email} />
        <DetailField label="SPID" value={<span className="font-mono">{detail.spid}</span>} />
        <DetailField label="Account Status" value={<StatusBadge status={detail.accountStatus} />} />
        <DetailField label="Verification" value={<VerificationBadge status={detail.verificationStatus} />} />
        <DetailField label="Created" value={formatDate(detail.createdAt)} />
        <DetailField
          label="Referral Agent"
          value={detail.isReferralAgent ? 'Yes' : 'No'}
        />
        {detail.isDeleted && (
          <DetailField label="Deleted" value={<span className="text-red-400">Yes</span>} />
        )}
      </div>

      {/* Suspension details */}
      {suspended && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
          <p className="text-xs font-semibold text-amber-300">Suspended {formatDate(detail.suspendedAt)}</p>
          {detail.suspendedReason && (
            <p className="text-xs text-amber-200/80 mt-1 break-words">{detail.suspendedReason}</p>
          )}
        </div>
      )}

      {/* Lifecycle actions */}
      <div className="border-t border-zinc-800 pt-4">
        <p className="text-xs font-semibold text-zinc-400 mb-2">Account lifecycle</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={suspended || isPending}
            onClick={() => onSuspend(detail)}
            aria-label="Suspend provider"
            className="min-h-[48px] px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          >
            Suspend
          </button>
          <button
            type="button"
            disabled={!suspended || isPending}
            onClick={() => onReactivate(detail)}
            aria-label="Reactivate provider"
            className="min-h-[48px] px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            Reactivate
          </button>
        </div>
      </div>

      {/* Audit trail */}
      <div className="border-t border-zinc-800 pt-4">
        <p className="text-xs font-semibold text-zinc-400 mb-2">Audit trail</p>
        <AuditTrail key={refreshKey} spid={detail.spid} initial={detail.recentAudit} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ProvidersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openSpid = searchParams.get('spid');

  // Filters / paging
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const s = searchParams.get('status');
    return s === 'Active' || s === 'Suspended' ? s : 'All';
  });
  const [page, setPage] = useState(1);

  // Data
  const [providers, setProviders] = useState<ProviderAdminListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Action state
  const [suspendTarget, setSuspendTarget] = useState<ProviderAdminDetail | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);
  const dismissToast = useCallback(() => setToast(null), []);

  // Debounce the search box into the query.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const beginLoad = useLatestRequest();

  const fetchProviders = useCallback(() => {
    const isCurrent = beginLoad();
    setIsLoading(true);
    setError(null);
    getProviders({
      status: statusFilter === 'All' ? undefined : statusFilter,
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((result) => {
        if (!isCurrent()) return;
        setProviders(result.items);
        setTotal(result.totalCount);
      })
      .catch((err: unknown) => {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : 'Failed to load providers');
      })
      .finally(() => {
        if (isCurrent()) setIsLoading(false);
      });
  }, [beginLoad, search, statusFilter, page]);

  useEffect(() => {
    startTransition(() => {
      void fetchProviders();
    });
  }, [fetchProviders]);

  function selectStatus(filter: StatusFilter) {
    setStatusFilter(filter);
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (filter === 'All') next.delete('status');
      else next.set('status', filter);
      return next;
    });
  }

  function openDrawer(spid: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('spid', String(spid));
      return next;
    });
  }

  function closeDrawer() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('spid');
      return next;
    });
  }

  async function handleConfirmSuspend(reason: string) {
    if (!suspendTarget) return;
    const target = suspendTarget;
    setActionPending(true);
    try {
      await suspendProvider(target.spid, reason);
      setToast({ variant: 'success', message: `Suspended ${displayLabel(target)}` });
      setSuspendTarget(null);
      setDetailRefreshKey((k) => k + 1);
      void fetchProviders();
    } catch (err) {
      setToast({
        variant: 'error',
        message:
          err instanceof ProviderNotFoundError
            ? 'Provider not found — it may have been removed.'
            : err instanceof Error
            ? err.message
            : 'Suspend failed',
      });
      if (err instanceof ProviderConflictError) {
        // State drifted (e.g. suspended elsewhere) — refresh to reflect reality.
        setSuspendTarget(null);
        setDetailRefreshKey((k) => k + 1);
        void fetchProviders();
      }
    } finally {
      setActionPending(false);
    }
  }

  async function handleReactivate(detail: ProviderAdminDetail) {
    setActionPending(true);
    try {
      await reactivateProvider(detail.spid);
      setToast({ variant: 'success', message: `Reactivated ${displayLabel(detail)}` });
      setDetailRefreshKey((k) => k + 1);
      void fetchProviders();
    } catch (err) {
      setToast({
        variant: 'error',
        message:
          err instanceof ProviderNotFoundError
            ? 'Provider not found — it may have been removed.'
            : err instanceof Error
            ? err.message
            : 'Reactivate failed',
      });
      if (err instanceof ProviderConflictError) {
        setDetailRefreshKey((k) => k + 1);
        void fetchProviders();
      }
    } finally {
      setActionPending(false);
    }
  }

  const selectedSpid = openSpid !== null ? Number(openSpid) : null;
  const selectedRow =
    selectedSpid !== null && Number.isFinite(selectedSpid)
      ? providers.find((p) => p.spid === selectedSpid) ?? null
      : null;

  const columns: Column<ProviderAdminListItem>[] = [
    {
      key: 'provider',
      label: 'Provider',
      render: (row) => (
        <div className="min-w-0">
          <p className="text-xs text-zinc-200 truncate max-w-xs" title={displayLabel(row)}>
            {displayLabel(row)}
          </p>
          {row.email && (
            <p className="text-xs text-zinc-500 truncate max-w-xs" title={row.email}>
              {row.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'accountStatus',
      label: 'Status',
      render: (row) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={row.accountStatus} />
          {row.isDeleted && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium bg-red-500/15 text-red-300 border-red-500/30">
              Deleted
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'verificationStatus',
      label: 'Verification',
      render: (row) => <VerificationBadge status={row.verificationStatus} />,
    },
    {
      key: 'isReferralAgent',
      label: 'Agent',
      render: (row) =>
        row.isReferralAgent ? (
          <span className="text-xs text-violet-300">✦ Agent</span>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => (
        <span className="text-xs font-mono text-zinc-500">{formatDate(row.createdAt)}</span>
      ),
    },
  ];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <div className="mr-2">
          <h1 className="text-sm font-semibold text-zinc-100">Service Providers</h1>
          <p className="text-xs text-zinc-500">Search providers and manage suspensions</p>
        </div>

        <div className="flex rounded-lg border border-zinc-700 overflow-hidden" role="group" aria-label="Filter by account status">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => selectStatus(filter)}
              aria-pressed={statusFilter === filter}
              className={[
                'px-3 py-1.5 text-xs font-medium min-h-[48px] transition-colors',
                statusFilter === filter
                  ? 'bg-violet-700 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
              ].join(' ')}
            >
              {filter}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by company or email…"
          aria-label="Search providers"
          className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 min-h-[48px] w-64"
        />

        <span className="ml-auto text-xs text-zinc-500">
          {total.toLocaleString()} providers
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table
          columns={columns}
          rows={providers}
          keyFn={(row) => String(row.spid)}
          onRowClick={(row) => openDrawer(row.spid)}
          isLoading={isLoading}
          emptyMessage="No providers match this filter."
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </div>

      {/* Detail drawer */}
      <Drawer
        open={selectedSpid !== null && Number.isFinite(selectedSpid)}
        onClose={closeDrawer}
        title={
          selectedRow ? (
            <div className="space-y-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-100 truncate" title={displayLabel(selectedRow)}>
                {displayLabel(selectedRow)}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={selectedRow.accountStatus} />
                <span className="text-xs text-zinc-500 font-mono">SPID {selectedRow.spid}</span>
              </div>
            </div>
          ) : (
            `Provider${selectedSpid !== null ? ` ${selectedSpid}` : ''}`
          )
        }
      >
        {selectedSpid !== null && Number.isFinite(selectedSpid) && (
          <ProviderDetailBody
            key={selectedSpid}
            spid={selectedSpid}
            isPending={actionPending}
            onSuspend={(detail) => setSuspendTarget(detail)}
            onReactivate={(detail) => void handleReactivate(detail)}
            refreshKey={detailRefreshKey}
          />
        )}
      </Drawer>

      {/* Suspend reason modal */}
      {suspendTarget && (
        <SuspendModal
          providerLabel={displayLabel(suspendTarget)}
          onConfirm={handleConfirmSuspend}
          onCancel={() => setSuspendTarget(null)}
        />
      )}

      {/* Toast */}
      <Toast toast={toast} onDismiss={dismissToast} />
    </main>
  );
}
